import {
  Button,
  Css,
  IconButton,
  ModalBody,
  MultiSelectField,
  TestIds,
  Tooltip,
  useComputed,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { fragment, ObjectState } from "@homebound/form-state";
import { observer, Observer } from "mobx-react";
import { ReadyPlanOptionGroupPersonalizationType } from "src/generated/graphqlTypes";
import { PlanEditorHeader } from "src/routes/components/PlanConfiguration/PlanEditorHeader";
import {
  PlaceholderMaterialSelection,
  PlaceholderProductSearch,
} from "src/routes/libraries/design-catalog/components/PlaceholderProductSearch";
import {
  EditPersonalizationOptionWizard_OptionCombinationFragment,
  EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment,
} from "src/routes/libraries/design-catalog/personalization-option/EditPersonalizationOptionWizard.generated";
import { PackageVariantsGridTable } from "src/routes/libraries/design-catalog/personalization-option/components/PackageVariantsGridTable";
import { StepNav } from "src/routes/libraries/design-catalog/personalization-option/components/StepNav";
import {
  buildRpoIdToRpogIdMap,
  canAssignTargetCombinations,
  FormValue,
  maybeGroupByFeature,
  PackageOption,
  removeDuplicateCombinations,
} from "src/routes/libraries/design-catalog/personalization-option/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/routes/libraries/product-catalog/components/product-images-viewer/ProductImageViewer";
import { fail } from "src/utils";

type VariantsStepProps = {
  formState: ObjectState<FormValue>;
  title: string;
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[];
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
  cancelUrl: string;
  isAdd: boolean;
  onSave: () => Promise<void>;
};

/** The second step: configure the product variants for the option. */
export function VariantsStep(props: VariantsStepProps) {
  const { formState, title, targetGroups, targetCombinations, cancelUrl, isAdd, onSave } = props;
  const finishLabel = isAdd ? "Create" : "Save Options";
  const tid = useTestIds({});
  const valid = useComputed(() => formState.valid, [formState]);

  return (
    <Observer>
      {() => {
        // Package matrix needs full width (horizontal scroll + sticky cols); product list stays centered.
        const isPackage = formState.type.value === ReadyPlanOptionGroupPersonalizationType.Package;
        return (
          // Fill the stepper area + hide overflow so the table region, not the page, scrolls.
          <div css={Css.bgWhite.fg1.df.fdc.oh.$} {...tid}>
            <PlanEditorHeader title={title} />
            {isPackage ? (
              // Full-width; the grid's own toolbar sits above the table.
              <div css={Css.df.fdc.fg1.oh.mw0.px5.pt3.$}>
                <PackageVariantsSection
                  formState={formState}
                  tid={tid}
                  targetCombinations={targetCombinations}
                  targetGroups={targetGroups}
                />
              </div>
            ) : (
              // Single/linked: centered, vertically-scrolling card list.
              <div css={Css.df.fdc.gap3.maxwPx(1000).w100.asc.py5.fg1.oya.mw0.$}>
                <h2 css={Css.xl.gray900.$}>Variants</h2>
                <ProductVariantsSection formState={formState} tid={tid} targetCombinations={targetCombinations} />
              </div>
            )}
            <StepNav
              cancelUrl={cancelUrl}
              disabled={!valid || formState.readOnly}
              onSave={onSave}
              saveLabel={finishLabel}
            />
          </div>
        );
      }}
    </Observer>
  );
}

/** The variants for linked/single product personalization options. */
const ProductVariantsSection = observer(function ProductVariantsSection(props: {
  tid: TestIds;
  formState: ObjectState<FormValue>;
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
}) {
  const { tid, formState, targetCombinations } = props;
  const { openModal } = useModal();
  // Assume we have at least 1 controlled placeholder, and we can use it as our "requirements" for the product search
  const placeholderTliId = formState.placeholders.value.first?.id;
  return (
    <div css={Css.df.fdc.gap2.$}>
      <InlineGrid /* product list */>
        {formState.productOptions.rows.map((productOption) => {
          const mv = productOption.data.value;
          return (
            <div key={mv.id} css={Css.df.fdc.wPx(208).tal.hPx(310).bshBasic.p2.$}>
              <div css={Css.df.aic.mb1.wPx(176).hPx(150).$}>
                <img
                  css={Css.m1.p1.br8.maxw100.maxh100.ma.p1.$}
                  src={mv.featuredImage?.asset?.previewUrl || PRODUCT_FALLBACK_IMG_URL}
                  alt={mv.displayName}
                />
              </div>
              <div css={Css.df.jcsb.aic.hPx(100).$}>
                <span css={Css.lineClamp4.$}>
                  {mv.listing.brand && (
                    <>
                      <label css={Css.smSb.$}>{mv.listing.brand.name}</label>{" "}
                    </>
                  )}
                  <Tooltip {...tid.materialName} title={mv.displayName} placement="top">
                    {mv.displayName}
                  </Tooltip>
                </span>
                <IconButton
                  icon="remove"
                  disabled={formState.readOnly}
                  onClick={() => formState.productOptions.remove(productOption.value)}
                  {...tid.materialRemove}
                />
              </div>
              {canAssignTargetCombinations(formState.type.value) && (
                <div css={Css.mt1.$}>
                  <MultiSelectField
                    label="Option Combinations"
                    disabled={targetCombinations.isEmpty}
                    options={targetCombinations}
                    placeholder="Select Option Combinations"
                    labelStyle="hidden"
                    onSelect={(val) => {
                      removeDuplicateCombinations(productOption, formState.productOptions.rows, val);
                      productOption.personalizationTargetCombinations.set(val.map((v) => ({ options: v.split(",") })));
                    }}
                    values={productOption.personalizationTargetCombinations.value.map((c) => c.options.join(","))}
                  />
                </div>
              )}
            </div>
          );
        })}
        {placeholderTliId && (
          <div css={Css.df.hPx(310).wPx(200).br8.bgGray100.$}>
            <div css={Css.ma.$}>
              <Button
                label="Add Variant"
                variant="secondary"
                icon="plus"
                disabled={formState.readOnly}
                onClick={() =>
                  openModal({
                    content: <ProductSearchModal placeholderTliId={placeholderTliId} formState={formState} />,
                    size: "xxl",
                  })
                }
              />
            </div>
          </div>
        )}
      </InlineGrid>
    </div>
  );
});

/** The variants for package product personalization options, rendered as a `GridTable`. */
const PackageVariantsSection = observer(function PackageVariantsSection(props: {
  tid: TestIds;
  formState: ObjectState<FormValue>;
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[];
}) {
  const { tid, formState, targetCombinations, targetGroups } = props;
  const { openModal } = useModal();
  const groupByFeature = !!formState.groupByFeature.value;

  // Determine the columns of the Package Options:
  // - When `groupByFeature=false`, we use a column per unique placeholder variant (i.e. all 32in sinks get 1 column)
  // - When `groupByFeature=true`, we use a column per unique placeholder + location pair (i.e. Tile+Wall, Tile+Floor are separate columns)
  // Because DPs already generalizes slots per placeholder/feature pair (20 cabinet knob assembly slots generalized to Kitchen > Island),
  // for `groupByFeature=true`, we can use our placeholder's location as-is, and trust that it's a) unique, and b) already feature-or-higher.
  // Respect the slot order set on the Details step; unordered slots sort to the end.
  const sortedPlaceholderRows = useComputed(
    () => [...formState.placeholders.rows].sortBy((row) => row.order.value ?? Infinity),
    [formState.placeholders],
  );
  const placeholderColumns = groupByFeature
    ? sortedPlaceholderRows
    : sortedPlaceholderRows.uniqueBy((p) => p.data.value.materialVariant?.id);

  // The Group By Feature toggle + Add Set live in the grid's toolbar so the only pinned chrome is the
  // page title and one toolbar row, leaving the table the rest of the height.
  const onToggleGroupByFeature = (value: boolean) => {
    formState.groupByFeature.set(value);
    maybeGroupByFeature(formState, value, buildRpoIdToRpogIdMap(targetGroups));
  };

  return (
    <PackageVariantsGridTable
      formState={formState}
      groupByFeature={groupByFeature}
      onToggleGroupByFeature={onToggleGroupByFeature}
      placeholderColumns={placeholderColumns}
      targetCombinations={targetCombinations}
      targetGroups={targetGroups}
      tid={tid}
      onOpenProductSearch={(placeholderTliId, packageOption) =>
        openModal({
          content: (
            <ProductSearchModal
              placeholderTliId={placeholderTliId}
              formState={formState}
              packageOption={packageOption}
            />
          ),
          size: "xxl",
        })
      }
    />
  );
});

/**
 * Wraps the `PlaceholderProductSearch` component in a modal.
 *
 * Unlike the existing DP "product search" flow, which is page/route based, we cannot leave the
 * page here b/c the user might have WIP changes to a new/unsaved RPOG.
 */
type ProductSearchModalProps = {
  placeholderTliId: string;
  formState: ObjectState<FormValue>;
  packageOption?: ObjectState<PackageOption>;
};

function ProductSearchModal(props: ProductSearchModalProps) {
  const { closeModal } = useModal();
  const { placeholderTliId, formState, packageOption } = props;
  const currentMaterials = useComputed<PlaceholderMaterialSelection>(() => {
    return { kind: "materials", ids: formState.productOptions.value.map((po) => po.materialVariantId) };
  }, [formState]);
  return (
    // Skipping `ModalTitle` b/c atm PlaceholderProductSearch has its own effectively-a-title UX
    <ModalBody>
      <PlaceholderProductSearch
        placeholderTliId={placeholderTliId}
        showNoMaterialVariant={!!packageOption}
        currentMaterials={currentMaterials}
        onMaterialSelected={async (selection) => {
          // mv is only undefined for "noMaterialVariant" selections, which can only happen for Package options
          const mv = selection.kind === "material" ? selection.mv : undefined;
          switch (formState.type.value) {
            case ReadyPlanOptionGroupPersonalizationType.LinkedProduct:
            case ReadyPlanOptionGroupPersonalizationType.SingleProduct:
              // Single/Linked are just a single pool of products, just make sure this one doesn't already exist
              if (mv && !formState.productOptions.value.some((po) => po.materialVariantId === mv.id)) {
                formState.productOptions.add({ materialVariantId: mv.id, data: fragment(mv) });
              }
              break;
            case ReadyPlanOptionGroupPersonalizationType.Package:
              if (!packageOption) fail("Expecting packageOption prop");
              const groupByFeature = formState.groupByFeature.value;
              // placeholderTlid will be our controlled placeholder
              const placeholder =
                formState.placeholders.rows.find((p) => p.id.value === placeholderTliId) ??
                fail(`Missing placeholder ${placeholderTliId}`);
              const productMaterial = mv ? { id: mv.id, data: mv } : undefined;
              // Package Option cells can only have a single product, so do an add if none yet, or update if existing
              const existingProduct = packageOption.products.rows.find(
                (p) =>
                  p.placeholderMaterialId.value === placeholder.data.value.materialVariant?.id &&
                  // If groupByFeature, our controlled placeholder will have DP already-generalized locations
                  // like `Bathroom` (room) or `Bathroom / Wall` (feature) so we can use it as-is
                  (groupByFeature ? p.locationId.value === placeholder.data.value.location.id : true),
              );
              if (existingProduct) {
                existingProduct.productMaterial.set(productMaterial);
              } else {
                packageOption.products.add({
                  placeholderMaterialId: placeholder.data.value.materialVariant!.id,
                  productMaterial,
                  // Even if !groupByFeature, we'll send up our controlled placeholder's location, but
                  // the backend will ignore it and use our RPOG.location
                  locationId: placeholder.data.value.location.id,
                });
              }
              break;
            default:
              return null;
          }
          closeModal();
        }}
      />
    </ModalBody>
  );
}

const InlineGrid = ({ children }: React.PropsWithChildren<unknown>) => (
  <div css={Css.dg.gtc("repeat(auto-fill, 200px)").gar("340px").gap2.mb4.$}>{children}</div>
);
